import React, { useState, useEffect } from 'react';
import { api } from '../context/AppContext';
import { Card, CardContent } from './ui/card';
import { Activity, TrendingDown, TrendingUp, Minus, AlertCircle, Loader2 } from 'lucide-react';

/**
 * Compact 4-week RIR histogram for the Evolution page.
 * Shows average Reps-in-Reserve per week with a qualitative trend banner.
 * Lower RIR = pushing harder (which is good for hypertrophy).
 */
const RirHistoryWidget = ({ language = 'fr' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const fr = language === 'fr';
  const t = (frTxt, enTxt) => (fr ? frTxt : enTxt);

  useEffect(() => {
    let cancelled = false;
    api.get('/workouts/rir-history?weeks=4')
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Card className="bg-card border-border" data-testid="rir-history-loading">
        <CardContent className="p-4 flex items-center justify-center">
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.weeks?.length) return null;

  const hasAnyData = data.global_samples > 0;
  const trend = data.trend || 'insufficient_data';
  const maxBarHeight = 64; // px

  // Trend palette
  const trendColors = {
    intensifying: { text: 'text-emerald-300', bg: 'bg-emerald-500/15', border: 'border-emerald-500/30', icon: TrendingDown },
    stable: { text: 'text-sky-300', bg: 'bg-sky-500/15', border: 'border-sky-500/30', icon: Minus },
    easing: { text: 'text-amber-300', bg: 'bg-amber-500/15', border: 'border-amber-500/30', icon: TrendingUp },
    insufficient_data: { text: 'text-muted-foreground', bg: 'bg-secondary/30', border: 'border-border/40', icon: AlertCircle },
  };
  const trendTheme = trendColors[trend] || trendColors.insufficient_data;
  const TrendIcon = trendTheme.icon;

  // Bar color per week — lower RIR = greener (more intense)
  const barColor = (rir) => {
    if (rir === null || rir === undefined) return 'bg-secondary/40';
    if (rir <= 1.5) return 'bg-emerald-400';
    if (rir <= 2.5) return 'bg-amber-400';
    return 'bg-orange-400';
  };

  return (
    <Card className="bg-card border-border" data-testid="rir-history-widget">
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1.5">
            <Activity className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-heading uppercase tracking-wide text-foreground">
              {t('Intensité RIR — 4 semaines', 'RIR Intensity — 4 weeks')}
            </h3>
          </div>
          {data.global_avg !== null && (
            <span className="text-[10px] text-muted-foreground" data-testid="rir-global-avg">
              {t('Moy.', 'Avg')} <span className="font-bold text-foreground">{data.global_avg}</span>
            </span>
          )}
        </div>

        {!hasAnyData ? (
          <p className="text-xs text-muted-foreground py-4 text-center">
            {t(
              'Logue ton RIR sur tes séries pour voir ton histogramme apparaître ici.',
              'Log RIR on your sets to see your histogram appear here.',
            )}
          </p>
        ) : (
          <>
            {/* Histogram — 4 bars */}
            <div className="flex items-end justify-around gap-2 h-20 mb-2" data-testid="rir-bars-row">
              {data.weeks.map((w, i) => {
                // Invert RIR for visual: lower RIR = taller bar (more intensity)
                const intensityPct = w.rir_avg !== null
                  ? Math.max(0.15, 1 - (w.rir_avg / 4)) // RIR 0 → 100%, RIR 4 → 0%
                  : 0.05;
                const height = Math.round(intensityPct * maxBarHeight);
                const isCurrent = w.week_offset === 0;
                return (
                  <div key={i} className="flex flex-col items-center flex-1 min-w-0">
                    {w.rir_avg !== null ? (
                      <span className="text-[10px] font-bold text-foreground mb-1" data-testid={`rir-bar-value-${i}`}>
                        {w.rir_avg}
                      </span>
                    ) : (
                      <span className="text-[10px] text-muted-foreground/40 mb-1">—</span>
                    )}
                    <div
                      className={`w-full max-w-[36px] rounded-t-md transition-all ${barColor(w.rir_avg)} ${isCurrent ? 'ring-2 ring-primary/50' : ''}`}
                      style={{ height: `${height}px` }}
                      data-testid={`rir-bar-${i}`}
                      aria-label={`Semaine ${w.label}: RIR ${w.rir_avg ?? 'aucune donnée'}`}
                    />
                    <span className={`text-[9px] uppercase tracking-wider mt-1 truncate w-full text-center ${
                      isCurrent ? 'text-primary font-bold' : 'text-muted-foreground'
                    }`}>
                      {w.label}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Trend banner */}
            <div className={`flex items-start gap-2 p-2.5 rounded-lg ${trendTheme.bg} border ${trendTheme.border} mt-3`} data-testid="rir-trend-banner">
              <TrendIcon className={`w-3.5 h-3.5 ${trendTheme.text} shrink-0 mt-0.5`} />
              <p className={`text-[11px] leading-snug ${trendTheme.text}`}>
                {fr ? data.trend_message_fr : data.trend_message_en}
              </p>
            </div>

            <p className="text-[9px] text-muted-foreground/60 mt-2 text-center italic">
              {t(
                'RIR plus bas = tu pousses plus fort. Vise 1-2 pour l\'hypertrophie.',
                'Lower RIR = pushing harder. Aim for 1-2 for hypertrophy.',
              )}
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default RirHistoryWidget;
